import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import flow from 'lodash/fp/flow';
import { makeStyles } from '@mui/styles';
import { remCalc } from '../../util/style-util';
import { resourceDarkGray, resourceDarkGray2, white } from '../../styles/style-constants';
import { AppStoreState, WorkbookState } from '../../types';
import { getTrialDaysRemaining } from 'state/modules/user/util';
import { toggleOnboardingNavigationMenuOverlay } from 'state/modules/ui/actions';
import { DEFAULT_TAG_TYPE } from './components/support-types';
import { fetchSupportResourcesForProduct } from 'state/modules/entity/custom/resource-favorites-mutations/thunks/resource-thunks';
import { FONT_GSS_FUTURA } from 'themes/default';
import { getCurrentWorkbook } from 'state/modules/entity/selectors/workbook-selectors';
import { getLicenseIsTrial } from 'state/modules/user/selectors/user-selectors';
import { toggleSlideoutMenu } from '../../state/modules/ui/actions';
import SupportVideos from './components/SupportVideos';
import SupportDocuments from './components/SupportDocuments';
import SupportLeftNavMenu from './components/SupportLeftNavMenu';
import Footer from '../../components/@common/PageFooter';
import { translate } from 'translations/translator';
import withModal from '@lwtears/lwt-common-frontend/lib/Modal/withModal';
import { UseModalDispatch } from '@lwtears/lwt-common-frontend/lib/Modal';
import { buildSearchQueryParams, parseQueryParams } from 'util/query-params-util';
import { resetValue } from 'state/modules/entity/actions';
import SupportTranslationVariant from 'translations/variants/support';
import config from 'config';

const translateSupport = (key: SupportTranslationVariant) => translate('support', key);

type Props = {
  isTrialLicense: boolean;
  trialDaysLeft: number;
  workbooksById: WorkbookState;
  workbook: WorkbookState;
  toggleSlideoutMenu: (boolean) => void;
  toggleOnboardingNavigationMenuOverlay: (boolean) => void;
  supportResources: any[];
  resetValue: (string) => void;
  fetchSupportResourcesForProduct: (productCurriculumId) => void;
} & UseModalDispatch;

const RenderSupportResource = ({ type }: any) => {
  switch (type) {
    case DEFAULT_TAG_TYPE.ADDITIONAL_RESOURCES:
    case DEFAULT_TAG_TYPE.ES_ADDITIONAL_RESOURCES:
      return <SupportDocuments />;
    default:
      return <SupportVideos />;
  }
};
const Support = (props: Props) => {
  const location = useLocation();
  const { trialDaysLeft }: any = props;

  const navigate = useNavigate();
  const params = useParams();

  const isTrialLicense = useSelector(state => getLicenseIsTrial(state, params));
  const workbook = useSelector(state => getCurrentWorkbook(state, params));

  useEffect(() => {
    props.fetchSupportResourcesForProduct(workbook?.products?.[0].productCurriculumId);

    return () => props.resetValue('supportResources');
  }, []);

  useEffect(() => {
    if (!props.supportResources) return;
    navigate({
      search: buildSearchQueryParams({
        active: props?.supportResources ? Object.keys(props?.supportResources)?.[0] : 0
      })
    });
  }, [props.supportResources]);

  const handleContactClick = (): Window | null =>
    window.open(config.digitalProductSupportUrl, '_blank');

  const { containerPage, container1, left, right, homeTileLeft, contactUs, contactUsLink } =
    useStyles()({ workbook });

  const queryParams = location.search ? parseQueryParams(location.search) : null;
  return (
    <section className={containerPage}>
      <div className={container1}>
        <div className={left}>
          <div className={homeTileLeft} onClick={() => {}}>
            <div
              style={{
                fontFamily: FONT_GSS_FUTURA,
                marginTop: 20,
                marginBottom: '5px',
                fontSize: '24px',
                fontWeight: 400
              }}
            >
              {translateSupport(SupportTranslationVariant.header)}
            </div>
            <SupportLeftNavMenu
              supportResources={props.supportResources && Object.keys(props.supportResources ?? {})}
            />
            <div className={contactUs}>
              <div style={{ fontWeight: 400 }}>
                {' '}
                {translateSupport(SupportTranslationVariant.contact)}
              </div>
              <div style={{ fontSize: 14, paddingTop: 20, fontWeight: 300 }}>
                {translateSupport(SupportTranslationVariant.officeHours1)}
                <br />
                {translateSupport(SupportTranslationVariant.officeHours2)}
                <br />
                <br />
                {translateSupport(SupportTranslationVariant.phone)}:<br />
                301.263.2700 | 1.888.983.8409
                <br />
              </div>
              <div className={contactUsLink} onClick={handleContactClick}>
                {translateSupport(SupportTranslationVariant.contactDescription)}
              </div>
            </div>
          </div>
        </div>
        <div className={right}>
          <RenderSupportResource type={queryParams?.active} />
        </div>
      </div>
      <Footer
        themeColor={workbook.primaryColor}
        textColor={workbook.textColor}
        isTrialLicense={isTrialLicense}
        trialDaysLeft={trialDaysLeft}
        showTrialModal={null}
      />
    </section>
  );
};

const useStyles = () =>
  makeStyles(() => ({
    homeTileLeft: {
      backgroundColor: white,
      opacity: 1.0,
      width: '290px',
      height: remCalc(650),
      //margin: remCalc(10),
      marginLeft: 55,
      marginTop: 40,
      paddingLeft: remCalc(20),
      //paddingRight: remCalc(20),
      paddingTop: remCalc(10),
      paddingBottom: remCalc(10),

      position: 'relative',
      boxShadow: '0px 3px 7px 1px' + resourceDarkGray,
      border: remCalc(1) + ' solid' + resourceDarkGray,
      borderRadius: remCalc(3),
      justifyContent: 'space-between',
      color: resourceDarkGray2,
      cursor: 'pointer',
      userSelect: 'none'
    },
    contactUs: {
      fontFace: FONT_GSS_FUTURA,
      marginTop: 20,
      marginBottom: '5px',
      fontSize: '16px',
      borderColor: '#DBDBDB',
      borderStyle: 'solid',
      borderRadius: '4px',
      borderWidth: '1px',
      paddingLeft: 15,
      paddingTop: 15,
      width: 248,
      height: 280
    },
    contactUsLink: {
      color: '#2878C1',
      paddingTop: 25,
      fontWeight: 400,
      fontSize: '14px',
      ':hover': {
        textDecoration: 'underline'
      }
    },
    iconCss: {
      height: remCalc(120),
      margin: 'auto',
      marginBottom: remCalc(10)
    },
    container1: {
      overflow: 'hidden',
      display: 'flex',
      height: '100%'
    },
    left: {
      height: '85vh',
      flex: '300px 0 0'
    },
    right: {
      flexGrow: 1,
      overflowY: 'auto'
    },
    containerPage: ({ workbook }: any) => ({
      height: 'calc(100% - 60px)',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: workbook?.secondaryColor
    }),
    mainContent: {
      flex: 1
    },
    headerContainer: {
      padding: remCalc(50, 0, 16)
    },
    headerContainerH2: {
      fontSize: 36,
      margin: 0,
      padding: '30px 0 10px',
      textAlign: 'center'
    },
    container: {
      overflow: 'auto',
      color: '#bca70b',
      fontSize: 32,
      marginTop: remCalc(200)
    },
    icon: {
      height: remCalc(100),
      margin: 'auto',
      marginBottom: remCalc(30)
    },
    mailIcon: {
      height: remCalc(90),
      margin: 'auto',
      marginTop: remCalc(5),
      marginBottom: remCalc(30)
    },
    supportButton: {
      background: '#ffffff',
      width: remCalc(325),
      height: remCalc(294),
      margin: remCalc(15),
      cursor: 'pointer',
      textAlign: 'center',
      boxShadow: '0 1px 8px 0 rgba(0,0,0,0.31)',
      outlineStyle: 'solid',
      outlineWidth: 'thin',
      outlineColor: '#aaaaaa',
      paddingTop: remCalc(36)
    },
    supportButtonHeader: {
      fontSize: 22,
      fontWeight: 500,
      paddingTop: remCalc(32)
    },
    supportButtonText: {
      fontSize: 18,
      fontWeight: 300
    }
  }));

const mapStateToProps = (state: AppStoreState) => ({
  trialDaysLeft: getTrialDaysRemaining(state.user),
  workbooksById: state.entity.workbooksById,
  user: state.user,
  supportResources: state.entity.supportResources
});

const withRedux = connect(mapStateToProps, {
  toggleSlideoutMenu,
  toggleOnboardingNavigationMenuOverlay,
  resetValue,
  fetchSupportResourcesForProduct
});

export default flow(withRedux, withModal)(Support);
